// Planes del cotizador: nombre y orden de cada plan, rango del enganche y mensaje de cada rechazo.
import { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';
import { ApiError, INTERNAL_ERROR_MESSAGE, userMessageOf } from '@/api/errors';
import type {
    StoreQuoterPlan,
    StoreQuoterSimulation,
} from '@/api/services/loan-offerings/quoter.contract';
import {
    downPaymentRange,
    pctsFromEffectiveTerms,
    UNKNOWN_DOWN_PAYMENT_PCTS,
    type DownPaymentPcts,
} from '@/domains/sales/wizard/downPaymentBounds';
import type { QuotePlan } from '@/domains/sales/wizard/types';
import { formatMXN } from '@/shared/lib/format/money';
import { tierLabel, type TierValue } from '@/shared/patterns/Pills/tierPresentation';

const LEVEL_TIER: Record<StoreQuoterPlan['level'], TierValue> = {
    BRONZE: ClientLevelEnum.BRONZE,
    SILVER: ClientLevelEnum.SILVER,
    GOLD: ClientLevelEnum.GOLD,
    ALL: null,
};

/** Orden de los chips del mock; lo que no es un nivel va al final. */
const TIER_ORDER: TierValue[] = [
    ClientLevelEnum.BRONZE,
    ClientLevelEnum.SILVER,
    ClientLevelEnum.GOLD,
    'employee',
];

const RATE = new Intl.NumberFormat('es-MX', { style: 'percent', maximumFractionDigits: 1 });

/** El nivel del cliente al que apunta el plan; el de empleado se reconoce por su tipo de venta. */
export function planTier(plan: StoreQuoterPlan): TierValue {
    return plan.saleTypes.includes('EMPLOYEE') ? 'employee' : LEVEL_TIER[plan.level];
}

/** «Oro», «Empleado», o el nombre del plan cuando no es de un nivel. */
export function planLabel(plan: StoreQuoterPlan): string {
    const tier = planTier(plan);
    return tier === null ? plan.name : tierLabel(tier, 'short');
}

function tierRank(plan: StoreQuoterPlan): number {
    const index = TIER_ORDER.indexOf(planTier(plan));
    return index === -1 ? TIER_ORDER.length : index;
}

export function sortPlans(plans: StoreQuoterPlan[]): StoreQuoterPlan[] {
    return [...plans].sort((a, b) => tierRank(a) - tierRank(b));
}

/** Como el mock, arranca en oro; sin oro, en el primero. */
export function defaultPlanId(plans: StoreQuoterPlan[]): string | null {
    const gold = plans.find((plan) => planTier(plan) === ClientLevelEnum.GOLD);
    return (gold ?? plans.at(0))?.planId ?? null;
}

export interface DownPaymentBounds {
    minCents: number;
    maxCents: number;
    minPct: number;
    maxPct: number | null;
}

/** Cotas que trae el plan o la cotización; el tope financiable también acota el enganche. */
export type DownPaymentTerms = Pick<
    StoreQuoterSimulation['effectiveTerms'],
    'minDownPaymentPct' | 'maxDownPaymentPct' | 'maxFinancedAmountCents'
>;

/** Pcts efectivos a este precio, con la misma regla que el paso de enganche del wizard. */
export function downPaymentPctsOf(terms: DownPaymentTerms, priceCents: number): DownPaymentPcts {
    return pctsFromEffectiveTerms(terms, priceCents) ?? UNKNOWN_DOWN_PAYMENT_PCTS;
}

/** Rango del enganche en cents: reusa el del wizard, que redondea al centavo. */
export function downPaymentBounds(
    priceCents: number,
    plan: StoreQuoterPlan,
    pcts: DownPaymentPcts,
): DownPaymentBounds {
    const quotePlan: QuotePlan = {
        planId: plan.planId,
        code: plan.code,
        minDownPaymentPct: plan.minDownPaymentPct,
        maxDownPaymentPct: plan.maxDownPaymentPct ?? undefined,
        termWeeksOptions: plan.termWeeksOptions,
        tnaAnnual: plan.tnaAnnual,
        openingCommissionPct: plan.openingCommissionPct,
    };
    const { min, max } = downPaymentRange(priceCents, quotePlan, pcts);
    return {
        minCents: Math.round(min * 100),
        maxCents: Math.round(max * 100),
        minPct: pcts.minPct ?? plan.minDownPaymentPct,
        maxPct: pcts.maxPct ?? plan.maxDownPaymentPct,
    };
}

/** El enganche elegido si cabe en el rango; si no, el mínimo. */
export function resolveDownPayment(chosen: number | null, bounds: DownPaymentBounds): number {
    if (chosen === null || chosen < bounds.minCents || chosen > bounds.maxCents) {
        return bounds.minCents;
    }
    return chosen;
}

export function formatRate(decimal: number): string {
    return RATE.format(decimal).replace(/\s/g, '');
}

function numberField(data: unknown, key: string): number | null {
    if (typeof data !== 'object' || data === null) return null;
    const value = (data as Record<string, unknown>)[key];
    return typeof value === 'number' ? value : null;
}

function termsField(data: unknown): number[] | null {
    if (typeof data !== 'object' || data === null) return null;
    const value = (data as Record<string, unknown>).termWeeksOptions;
    return Array.isArray(value) && value.every((term) => typeof term === 'number') ? value : null;
}

/** «8, 12 o 16». */
function joinTerms(terms: number[]): string {
    const labels = terms.map(String);
    return labels.length < 2
        ? labels.join('')
        : `${labels.slice(0, -1).join(', ')} o ${String(labels.at(-1))}`;
}

type MessageBuilder = (data: unknown, plan: string) => string;

const MESSAGE_BY_CODE: Record<string, MessageBuilder> = {
    DOWN_PAYMENT_TOO_LOW: (data, plan) => {
        const min = numberField(data, 'minDownPaymentPct');
        return min === null
            ? `El enganche está por debajo del mínimo del plan ${plan}. Súbelo para cotizar.`
            : `El enganche mínimo del plan ${plan} es del ${formatRate(min)}. Súbelo para cotizar.`;
    },
    DOWN_PAYMENT_TOO_HIGH: (data, plan) => {
        const max = numberField(data, 'maxDownPaymentPct');
        return max === null
            ? `El enganche pasa del máximo del plan ${plan}. Bájalo para cotizar.`
            : `El enganche máximo del plan ${plan} es del ${formatRate(max)}. Bájalo para cotizar.`;
    },
    AMOUNT_EXCEEDS_LIMIT: (data, plan) => {
        const max = numberField(data, 'maxFinancedAmountCents');
        const limit = max === null ? '' : ` (${formatMXN(max / 100)})`;
        return `El monto financiado excede el máximo del plan ${plan}${limit}. Sube el enganche o elige otro nivel.`;
    },
    TERM_NOT_AVAILABLE: (data, plan) => {
        const terms = termsField(data);
        const options = terms && terms.length > 0 ? ` Elige ${joinTerms(terms)} semanas.` : '';
        return `Ese plazo no está disponible en el plan ${plan}.${options}`;
    },
    INVALID_PLAN_PARAMETERS: (_data, plan) =>
        `El plan ${plan} tiene una configuración que no permite cotizar. Avisa a tu gerente.`,
    STORE_NOT_FOUND: () => 'Tu tienda no aparece en el padrón. Pide a tu gerente que la revise.',
    PLAN_NOT_OFFERED_TO_STORE: (_data, plan) =>
        `El plan ${plan} ya no está disponible en tu tienda. Elige otro nivel.`,
    CREDIT_PLAN_NOT_FOUND: (_data, plan) => `El plan ${plan} ya no existe. Elige otro nivel.`,
    PLAN_INACTIVE: (_data, plan) => `El plan ${plan} está inactivo. Elige otro nivel.`,
    STORE_POLICY_MAKES_PLAN_UNQUOTABLE: (_data, plan) =>
        `Una política de tu tienda no permite cotizar el plan ${plan}. Elige otro nivel.`,
    STORE_POLICY_SUSPENDED: () =>
        'Las ventas a crédito de tu tienda están suspendidas por una política vigente.',
    SKU_NOT_ALLOWED: () => 'Este equipo no se puede vender a crédito en tu tienda. Elige otro.',
    SCI_BELOW_STORE_POLICY_FLOOR: (_data, plan) =>
        `Tu tienda pide un nivel mayor que ${plan} para vender a crédito. Elige otro nivel.`,
    STORE_POLICY_SCOPE_UNKNOWN: () =>
        'Una política de tu tienda está mal configurada. Avisa a tu gerente.',
    VALIDATION_ERROR: () => 'Revisa el precio, el enganche y el plazo de la cotización.',
};

/** El mensaje útil de un rechazo según su `code`; un 5xx da el genérico y lo demás el de la API. */
export function quoterErrorMessage(error: unknown, planName: string): string {
    if (error instanceof ApiError && Object.hasOwn(MESSAGE_BY_CODE, error.code)) {
        return MESSAGE_BY_CODE[error.code](error.data, planName.toLowerCase());
    }
    if (error instanceof ApiError && error.httpStatus >= 500) return INTERNAL_ERROR_MESSAGE;
    return userMessageOf(error);
}
